/* eslint-disable @typescript-eslint/no-explicit-any */
// eslint disabled for this particular rule / this file because it's too much effort to define types for
// the whole import xml
import { parseString } from 'xml2js'
import {
  BMP,
  COST,
  DATA,
  FileSelectionImage,
  IMAGE,
  INFO,
  JNL,
  LeakageExport,
  LeakCamExport,
  LeakImage,
  LeakImageRaw,
  RES,
} from './dataImportTypes'
import { journalToImportCompanies } from './dataImportConversion'
import { DataImportTarget, DatePickerDates, ImportCompany, ImportProject } from '../../../interfaces/DataImport'
import { identifyXmlOrigin, origins } from './dataImportAuthorization'
import { Leakage } from '../../../interfaces/BusinessObjects'
import he from 'he'
import { Brand, environment } from '../../../constants/environment'
import * as path from 'path'

type HasRoot = {
  $: COST | RES | INFO | DATA | BMP
}

export type DataImportRequest = {
  companies: ImportCompany[]
  uids?: string[]
  images?: FileSelectionImage[]
  target?: DataImportTarget
  journalPath?: string
  error: boolean
}

export const extractBmp = (jnl: any): BMP => {
  return getObjectRoot(jnl.BMP) as BMP
}

const extractCost = (jnl: any): COST => {
  // return getObjectRoot(jnl.COST) as COST
  const parts = jnl.COST.map((i: any) => i.$)
  const data = join(parts)
  return data as COST
}

const extractRes = (jnl: any): RES => {
  return getObjectRoot(jnl.RES) as RES
}

function join<T>(parts: T[]): T {
  const data = {}
  parts.map((p: T) => Object.assign(data, p))
  const keys = Object.keys(data)
  // @ts-ignore
  keys.map((key) => (data[key] = he.decode(data[key])))
  return data as T
}

const extractInfo = (jnl: any): INFO => {
  const parts = jnl.INFO.map((i: any) => i.$)
  const data: INFO = join(parts)
  return data
}

const extractData = (jnl: any): DATA => {
  const parts = jnl.DATA.map((i: any) => i.$)
  const data = join(parts)
  return data as DATA
}

export const getObjectRoot = (o: Array<HasRoot>): COST | RES | INFO | DATA | BMP => {
  return o[0].$
}

export const parseJnl = (jnl: any): JNL => {
  const DATA = extractData(jnl)
  const INFO = extractInfo(jnl)
  const RES = extractRes(jnl)
  const BMP = extractBmp(jnl)
  const COST = extractCost(jnl)
  return { DATA, INFO, RES, BMP, COST, PAYLOAD: jnl }
}

const checkOriginAuthorized = (journal: JNL[]): boolean => {
  const results = journal.map((jnl) => identifyXmlOrigin(parseInt(jnl.INFO.DT32), parseInt(jnl.INFO.DT33)))
  if (environment.BRAND === Brand.Testo) {
    return results.includes(origins.TESTO)
  }
  if (environment.BRAND === Brand.Beko) {
    return results.includes(origins.BEKO)
  }
  return true
}
const getUnitV = (lossUnit?: string): string => {
  if (lossUnit === 'cfm') {
    return 'SCF'
  }
  return 'Nm³'
}

export const cleanXmlJournal = (xml: string): string => {
  return xml.replace(/".*?"/g, (match: string) => {
    const newMatch = match
      .slice(1, -1)
      .replace('&', '&amp;')
      .replace('<', '&lt;')
      .replace('>', `&gt;`)
      .replace('"', '&apos;')
      .replace(`'`, '&quot;')
      .replace('', '')
      .replace('', '')
      .replace('', '')
      .replace('', '')
    return '"' + newMatch + '"'
  })
}

const transformJsonLeakCamExport = (content: string): LeakCamExport => {
  const data = JSON.parse(content) as LeakCamExport
  // const data2 = JSON.parse(content) as LeakCamExport

  data.leakages?.forEach((leak, li) => {
    leak.leakImages = leak.leakImages?.map((rawImg, ri) => {
      const img: LeakImage = {
        imageId: rawImg.imageId,
        originalImagePath: rawImg.originalImagePath,
        overlaidImagePath: rawImg.overlaidImagePath,
      }
      /*

      if (isValidJson(rawImg.beamformerConfig)) {
        try {
          img.beamformerConfig = JSON.parse(rawImg.beamformerConfig as string)
        } catch (e) {
          console.error(`   ✖ parse error beamformerConfig bei Leak[${li}]/Img[${ri}] LeakageId: ${leak.id}`, e)
        }
      }

      if (isValidJson(rawImg.acousticImageMetaData)) {
        try {
          img.acousticImageMetaData = JSON.parse(rawImg.acousticImageMetaData as string)
        } catch (e) {
          console.error(`   ✖ parse error acousticImageMetaData bei Leak[${li}]/Img[${ri}] LeakageId: ${leak.id}`, e)
        }
      }
*/

      return img
    })
  })

  return data
}

export const processImport = (
  content: string,
  dataImportText: string,
  cb: (result: DataImportRequest) => void,
  journalPath: string,
): void => {
  const dateString = new Date().toLocaleDateString('de-de', {
    hour: 'numeric',
    minute: 'numeric',
  })
  const isJson = /\.json$/i.test(journalPath)
  const isXml = /\.xml$/i.test(journalPath)

  // Helper to build companies and return success in ONE place
  const finalizeSuccess = (journal: JNL[], leakCamExport?: LeakCamExport): void => {
    const companies = journalToImportCompanies(journal)
    companies.forEach((c, i, cs) => {
      const name = `${dataImportText} - ${dateString}`
      cs[i].projects = c.projects.map((p) => {
        p.name = name
        p.description = name
        p.leakCamRawData = isJson ? JSON.stringify(leakCamExport) : undefined
        return p
      })
    })
    cb({
      error: false,
      companies,
    })
  }

  // JSON branch (sync)
  if (isJson) {
    if (!isValidJson(content)) {
      cb({
        error: true,
        companies: [],
        journalPath,
      })
      return
    }

    const leakCamExport = transformJsonLeakCamExport(content)
    const journal = transformJsonToJnl(leakCamExport)

    if (!checkOriginAuthorized(journal)) {
      cb({
        error: true,
        companies: [],
      })
      return
    }

    finalizeSuccess(journal, leakCamExport)
    return
  }

  // XML branch (async via callback)
  if (isXml) {
    try {
      parseString(cleanXmlJournal(content), (err: Error, result: any) => {
        if (err) {
          console.error(err)
          cb({
            error: true,
            companies: [],
            journalPath,
          })
          return
        }

        const journal: JNL[] = result.JOURNAL.JNL.map((jnl: any) => parseJnl(jnl))

        if (!checkOriginAuthorized(journal)) {
          cb({
            error: true,
            companies: [],
          })
          return
        }

        finalizeSuccess(journal)
      })
    } catch (e) {
      console.error(e)
      cb({
        error: true,
        companies: [],
        journalPath,
      })
    }
    return
  }

  // Unsupported file type
  cb({
    error: true,
    companies: [],
    journalPath,
  })
}

const convertStringToDate = (date: string, time: string): Date => {
  const dates = date.split('.')
  const times = time.split(':')
  return new Date(
    Number(dates[2]),
    Number(dates[1]) - 1,
    Number(dates[0]),
    Number(times[0]),
    Number(times[1]),
    Number(times[2]),
    0,
  )
}

export const getFirstAndLastDateFromLeakagesFromRequests = (
  request: DataImportRequest | undefined,
): DatePickerDates => {
  if (!request) {
    return {
      first: new Date(),
      last: new Date(),
    }
  }
  const projectsRequest = request.companies
    .reduce((newProjects: any, company: any) => {
      newProjects.push(company.projects)
      return newProjects
    }, [])
    .flat() as ImportProject[]
  const leakagesRequest = projectsRequest
    .reduce((newLeakages: any, project: any) => {
      newLeakages.push(project.leakages)
      return newLeakages
    }, [])
    .flat()
    .sort(
      (a: any, b: any) => convertStringToDate(a.date, a.time).getTime() - convertStringToDate(b.date, b.time).getTime(),
    ) as Leakage[]

  const firstLeakage = leakagesRequest.shift()
  const lastLeakage = leakagesRequest.length === 0 ? firstLeakage : leakagesRequest.pop()

  return {
    first: convertStringToDate(firstLeakage?.date || '', firstLeakage?.time || ''),
    last: convertStringToDate(lastLeakage?.date || '', lastLeakage?.time || ''),
  }
}

const extractDataLeakCam = (leakage: LeakageExport, leakCamExport: LeakCamExport): DATA => {
  // const thresholdDb = leakage?.leakImages?.pop()?.acousticImageMetaData?.thresholdDb
  const thresholdDb = 10
  const data: DATA = {
    Distance: leakage.distance ? `${leakage.distance}` : '',
    Gain: '',
    GasNType: '',
    GasType: leakage.gasType ? `${leakage.gasType}` : '',
    Level: leakage.level ? `${leakage.level}` : '',
    Sens2x: '',
    Sensivity: '',
    Sign: '',
    Sysbase: '',
    Threshold: thresholdDb ? `${thresholdDb}` : '', // ThresholdDb
    ToolN: '',
    ToolName: '',
    ToolSN: '',
    UnitD: leakage.distanceType ? `${leakage.distanceType}` : '',
    UnitL: leakage.lossUnit ? `${leakage.lossUnit}` : '',
    UnitND: '',
    UnitNL: '',
    UnitNP: '',
    UnitNV: '',
    UnitP: leakage.pressureType ? `${leakage.pressureType}` : '',
    Pressure: leakage.pressure ? `${leakage.pressure}` : '',
  }
  return data
}

function formatUnixTimestamp(timestampSeconds: number): { date: string; time: string } {
  // Date-Objekt aus Sekunden bauen (JS erwartet Millisekunden)
  const dateObj = new Date(timestampSeconds * 1000)

  // Hilfsfunktion zum Auffüllen mit führender Null
  const pad2 = (n: number): string => n.toString().padStart(2, '0')

  const day = pad2(dateObj.getDate())
  const month = pad2(dateObj.getMonth() + 1) // getMonth() geht 0–11
  const year = dateObj.getFullYear()

  const hours = pad2(dateObj.getHours())
  const minutes = pad2(dateObj.getMinutes())
  const seconds = pad2(dateObj.getSeconds())

  return {
    date: `${day}.${month}.${year}`,
    time: `${hours}:${minutes}:${seconds}`,
  }
}
function getComRup(resolved?: boolean, resolvableUnderPressure?: boolean): string {
  // 1) Beide undefined → "0x00"
  if (resolved === undefined && resolvableUnderPressure === undefined) {
    return '0x00'
  }

  // 2) Status = "Keine Eingabe", aber Unter Druck gegeben
  if (resolved === undefined) {
    if (resolvableUnderPressure === true) {
      return '0x01' // Keine Eingabe / Ja
    }
    if (resolvableUnderPressure === false) {
      return '0x02' // Keine Eingabe / Nein
    }
  }

  // 3) Status = "Behoben"
  if (resolved === true) {
    // a) Unter Druck = undefined → "0x10"
    if (resolvableUnderPressure === undefined) {
      return '0x10' // Behoben / Keine Eingabe
    }
    // b) Unter Druck = true → "0x11"
    if (resolvableUnderPressure) {
      return '0x11' // Behoben / Ja
    }
    // resolved = true & resolvableUnderPressure = false --> invalid
  }

  // 4) Status = "Nicht behoben"
  if (resolved === false) {
    // a) Unter Druck = undefined → "0x20"
    if (resolvableUnderPressure === undefined) {
      return '0x20' // Nicht behoben / Keine Eingabe
    }
    // b) Unter Druck = false → "0x22"
    if (!resolvableUnderPressure) {
      return '0x22' // Nicht behoben / Nein
    }
    // resolved = false & resolvableUnderPressure = true --> invalid
  }

  // Alle anderen Kombinationen sind nicht definiert:
  throw new Error('Ungültige Kombination: Status und Unter Druck passen nicht zur Tabelle.')
}

const extractInfoLeakCam = (leakage: LeakageExport, leakCamExport: LeakCamExport): INFO => {
  const commF = leakCamExport?.failures?.find((f) => f.id === leakage.failureId)
  const commM = leakCamExport?.measures?.find((m) => m.id === leakage.measureId)
  const commR = leakCamExport?.replacements?.find((r) => r.id === leakage.replacementId)
  const company = leakCamExport?.companies?.find((c) => c.id === leakage.companyId)
  const dateAndTime = formatUnixTimestamp(leakage?.createdAt || 0)
  const building = leakCamExport?.buildings?.find((b) => b.id === leakage.buildingId)
  const place = leakCamExport?.places?.find((p) => p.Id === leakage.placeId)

  const info: INFO = {
    CommF: commF?.name || '',
    CommM: commM?.name || '',
    CommR: commR?.name || '',
    CommRup: getComRup(leakage.resolved, leakage.resolvableUnderPressure),
    Comment: leakage?.comment || '',
    Company: company?.name || 'unknown',
    DT32: leakCamExport.deviceInfo.dt32,
    DT33: leakCamExport.deviceInfo.dt33,
    Date: dateAndTime.date,
    Time: dateAndTime.time,
    LeakTag: leakage.leakTag ? `${leakage.leakTag}` : '',
    Location: building?.name ? `${building?.name}` : '',
    Place: place?.Name ? `${place?.Name}` : '',
    SN: '',
    SW: '',
    SigE2: '',
    UID: leakage.id ? `${leakage.id}` : '',
    Version: '',
  }
  return info
}

const extractResLeakCam = (leakage: LeakageExport, leakCamExport: LeakCamExport): RES => {
  const res: RES = {
    Cost: leakage.lossCosts ? `${leakage.lossCosts}` : '', //LossCosts
    Leakage: leakage.loss ? `${leakage.loss}` : '', // Loss
    Sign: '',
    UnitL: leakage.lossUnit ? `${leakage.lossUnit}` : '', //LossUnit
  }
  return res
}

const extractBmpLeakCam = (leakage: LeakageExport, leakCamExport: LeakCamExport): BMP => {
  const image =
    leakage?.leakImages && leakage.leakImages.length > 0 ? leakage.leakImages[leakage.leakImages.length - 1] : undefined
  if (image && image?.overlaidImagePath) {
    const Path = path.dirname(image.overlaidImagePath ?? '')
    const Name = path.basename(image.overlaidImagePath ?? '')
    return { Name, Path }
  }
  return { Name: '', Path: '' }
}

const extractCostLeakCam = (leakage: LeakageExport, leakCamExport: LeakCamExport): COST => {
  const cost: COST = {
    Base: leakage?.costPerVolume ? `${leakage.costPerVolume}` : '',
    CMod: getCModFromJson(leakage, leakCamExport), // standard, expert?
    ElPr: leakage?.electricityPrice ? `${leakage.electricityPrice}` : '',
    SigE3: '', // benötigen wir nicht
    SpPw: leakage?.specificPower ? `${leakage.specificPower}` : '',
    SpPwId: '', // benötigen wir nicht
    Time: leakage?.operatingHoursYear ? `${leakage.operatingHoursYear}` : '',
    USpPw: '', // benötigen wir nicht
    UnitC: leakage?.currency ? `${leakage.currency}` : '',
    UnitV: getUnitV(leakage?.lossUnit),
  }
  return cost
}

const extractImagesLeakCam = (leakage: LeakageExport, leakCamExport: LeakCamExport): IMAGE[] | undefined => {
  let images = undefined
  if (leakage.leakImages && leakage.leakImages.length > 0) {
    images = leakage.leakImages.map((image) => {
      const imgPath = path.dirname(image.overlaidImagePath ?? '')
      const imgName = path.basename(image.overlaidImagePath ?? '')
      return { name: imgName, path: imgPath }
    })
  }
  return images
}

const getCModFromJson = (leakage: LeakageExport, leakCamExport: LeakCamExport): string => {
  if (leakage.costMode?.includes('expert')) {
    // electrictityCost --> 70%
    if (leakage.costCalculationMode?.includes('electricityCost')) {
      return '0x01'
    }
    // totalCost -->100%
    if (leakage.costCalculationMode?.includes('totalCost')) {
      return '0x03'
    }
  }
  return '0x00'
}

const transformJsonToJnl = (leakCamExport: LeakCamExport): JNL[] => {
  const journals: JNL[] = []
  leakCamExport.leakages?.map((leakage) => {
    const data = extractDataLeakCam(leakage, leakCamExport)
    const info = extractInfoLeakCam(leakage, leakCamExport)
    const res = extractResLeakCam(leakage, leakCamExport)
    const images = extractImagesLeakCam(leakage, leakCamExport)
    const bmp = extractBmpLeakCam(leakage, leakCamExport)
    const cost = extractCostLeakCam(leakage, leakCamExport)
    const journal: JNL = {
      DATA: data,
      INFO: info,
      RES: res,
      BMP: bmp,
      COST: cost,
      IMAGES: images,
    }
    journals.push(journal)
  })
  return journals
}

export function isValidJson(input: unknown): boolean {
  if (typeof input !== 'string') {
    return false
  }
  try {
    JSON.parse(input)
    return true
  } catch {
    return false
  }
}
